// App.js
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { MotionConfig } from "framer-motion";

import Navigation from "./components/Navigation/Navigation";
import HomeSection from "./components/HomeSection/Home";
import AchievementsSection from "./components/AchievementSection/Achievement";
import AboutSection from "./components/AboutSection/About";
import ExperienceSection from "./components/ExperienceSection/Experience";
import EducationSection from "./components/EducationSection/Education";
import ProjectsSection from "./components/ProjectSection/Project";
import ContactSection from "./components/ContactSection/Contact";
import FooterSection from "./components/FooterSection/footer";
import ChatOverlay from "./components/ChatSection/Chat";   // floating chat
import FullPageChat from "./components/FullPageChatSection/FullPageChat";// full page chat
import WeatherTracker from "./components/WeatherTracker/Weather";
import CVPage from "./components/CVPage/CVPage";
import NotFound from "./components/NotFound/NotFound";
import CommandPalette from "./components/CommandPalette/CommandPalette";
import { CHAT_NAVIGATE_EVENT } from "./components/ChatSection/chatEvents";
import SmoothScroll from "./scroll/SmoothScroll";
import useReveal from "./scroll/useReveal";
import './App.css';

function MainPage() {
  const [activeSection, setActiveSection] = useState('Home');
  // Technology picked in Tools & Technologies; the projects grid shows only what uses it
  const [techFilter, setTechFilter] = useState(null);

  useReveal();

  useEffect(() => {
    const ids = ['Home', 'projects', 'about', 'experience', 'skills', 'education', 'contact'];
    let frame = 0;
    // One layout read per frame at most, however fast the wheel fires
    const update = () => {
      frame = 0;
      const y = window.scrollY + 100;
      const current = ids.find((id) => {
        const el = document.getElementById(id);
        return el && y >= el.offsetTop && y < el.offsetTop + el.offsetHeight;
      });
      if (current) setActiveSection(current);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <>
      <Navigation activeSection={activeSection} />
      <ChatOverlay />
      <CommandPalette />
      <HomeSection activeSection={activeSection} />
      <ProjectsSection techFilter={techFilter} onTechFilter={setTechFilter} />
      <AboutSection/>
      <ExperienceSection />
      <AchievementsSection techFilter={techFilter} onTechFilter={setTechFilter} />
      <EducationSection />
      <ContactSection />
      <FooterSection />
    </>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const navigate = useNavigate();

  // Links in chat answers (e.g. a project write-up) open in place
  useEffect(() => {
    const onNavigate = (e) => navigate(e.detail.path);
    window.addEventListener(CHAT_NAVIGATE_EVENT, onNavigate);
    return () => window.removeEventListener(CHAT_NAVIGATE_EVENT, onNavigate);
  }, [navigate]);

  return (
    <>
      <SmoothScroll />
      <Routes location={location}>
        {/* Main page. Projects and certificates open as dialogs with their own shareable URLs. */}
        <Route path="/" element={<MainPage />}>
          <Route path="projects/:slug" element={null} />
          <Route path="certificates/:slug" element={null} />
        </Route>

        {/* Weather page */}
        <Route path="/weather" element={<WeatherTracker />} />

        {/* Full Page Chat page */}
        <Route path="/chat" element={<FullPageChat />} />

        {/* CV page */}
        <Route path="/cv" element={<CVPage />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <MotionConfig reducedMotion="user">
    <Router>
      <div className="background">
        <AnimatedRoutes />
      </div>
    </Router>
    </MotionConfig>
  );
}

export default App;
